import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiPrinter, FiCheckCircle, FiDownload } from 'react-icons/fi';
import api from '../../utils/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const getStatusStyle = (status) => {
    if (status === 'approved') return { bg: '#ecfdf5', color: C.primary, label: 'Approved' };
    if (status === 'rejected') return { bg: '#fef2f2', color: C.red, label: 'Rejected' };
    if (status === 'draft') return { bg: '#f1f5f9', color: '#94a3b8', label: 'Draft' };
    return { bg: '#fffbeb', color: C.orange, label: 'Submitted' };
};

const TeacherViewPaper = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [paper, setPaper] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });

    useEffect(() => {
        const fetchPaperAndSubmissions = async () => {
            try {
                const res = await api.get(`/api/teacher/papers/${id}`);
                const fetchedPaper = res.data.paper;

                setPaper(fetchedPaper);
                setQuestions(res.data.questions);

                // If approved, fetch submissions too
                if (fetchedPaper.status === 'approved') {
                    try {
                        const subRes = await api.get(`/api/teacher/papers/${id}/submissions`);
                        setSubmissions(subRes.data.submissions || []);
                    } catch (err) {
                        console.error('Failed to fetch submissions', err);
                    }
                }
            } catch (err) {
                console.error(err);
                if (err.response?.status === 403) {
                    setErrorModal({ show: true, message: err.response.data.message });
                } else {
                    alert(err.response?.data?.message || 'Failed to load paper details.');
                    navigate('/teacher/papers');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchPaperAndSubmissions();
    }, [id, navigate]);

    const downloadPDF = async () => {
        if (!paper) return;

        // Dynamically import html2canvas
        const html2canvas = (await import('html2canvas')).default;

        // Create a temporary container with the paper content styled for printing
        const container = document.createElement('div');
        container.style.cssText = 'position:absolute;left:-9999px;top:0;width:794px;padding:40px 50px;background:#fff;font-family:Arial,sans-serif;color:#000;line-height:1.5;';
        
        let html = `
            <div style="text-align:center;margin-bottom:20px;">
                <h1 style="margin:0;font-size:22px;color:#141e46;">SWAMI VIVEKANANDA UNIVERSITY</h1>
                <p style="margin:4px 0;font-size:14px;color:#666;">${paper.department || 'Academic Department'}</p>
                <hr style="border:none;border-top:1px solid #ccc;margin:10px 0;"/>
                <h2 style="margin:6px 0;font-size:16px;">${paper.title}</h2>
            </div>
            <table style="width:100%;font-size:12px;margin-bottom:16px;border-collapse:collapse;">
                <tr><td style="padding:3px 0;"><b>Subject:</b></td><td style="padding:3px 0;">${paper.subject_code} - ${paper.subject_name}</td></tr>
                <tr><td style="padding:3px 0;"><b>Batch:</b></td><td style="padding:3px 0;">${paper.batch_name || 'N/A'}</td></tr>
                <tr><td style="padding:3px 0;"><b>Duration:</b></td><td style="padding:3px 0;">${paper.duration_mins} Minutes</td></tr>
                <tr><td style="padding:3px 0;"><b>Total Marks:</b></td><td style="padding:3px 0;">${paper.total_marks}</td></tr>
            </table>
        `;

        if (paper.instructions) {
            html += `<div style="margin-bottom:16px;"><b style="font-size:12px;">General Instructions:</b><p style="font-size:12px;margin:4px 0;">${paper.instructions}</p></div>`;
        }

        html += `<hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>`;
        html += `<h3 style="font-size:14px;margin:0 0 12px;">QUESTIONS</h3>`;

        questions.forEach((q, idx) => {
            html += `<div style="margin-bottom:14px;">`;
            html += `<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px;">`;
            html += `<div style="flex:1;font-size:12px;"><b>Q${idx + 1}.</b> ${q.question_text}</div>`;
            html += `<div style="font-size:11px;color:#666;white-space:nowrap;margin-left:12px;">[${q.marks} Marks]</div>`;
            html += `</div>`;

            if (q.question_type === 'mcq') {
                const opts = [
                    { key: 'A', val: q.option_a },
                    { key: 'B', val: q.option_b },
                    { key: 'C', val: q.option_c },
                    { key: 'D', val: q.option_d }
                ].filter(o => o.val);
                html += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:2px 20px;margin-left:20px;font-size:12px;">`;
                opts.forEach(o => {
                    html += `<div>(${o.key}) ${o.val}</div>`;
                });
                html += `</div>`;
            }
            html += `</div>`;
        });

        container.innerHTML = html;
        document.body.appendChild(container);

        try {
            const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
            document.body.removeChild(container);

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const imgWidth = 190; // A4 width minus margins
            const pageHeight = 277; // A4 height minus margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const doc = new jsPDF('p', 'mm', 'a4');
            let heightLeft = imgHeight;
            let position = 10;
            let page = 1;

            // First page
            doc.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            // Additional pages if content is long
            while (heightLeft > 0) {
                position = heightLeft - imgHeight + 10;
                doc.addPage();
                doc.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
                page++;
            }

            // Footer on all pages
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(9);
                doc.setTextColor(150);
                doc.text(`Page ${i} of ${totalPages} | SVU Exam Portal`, 105, 290, { align: 'center' });
            }

            doc.save(`${paper.subject_code}_${paper.title.replace(/\s+/g, '_')}.pdf`);
        } catch (err) {
            document.body.removeChild(container);
            console.error('PDF generation error:', err);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    if (loading) {
        return <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontFamily: font }}>Loading paper details...</div>;
    }

    if (errorModal.show) {
        return (
            <div style={{ padding: '20px 10px', fontFamily: font, maxWidth: 800, margin: '0 auto' }}>
                <button onClick={() => navigate('/teacher/papers')} style={{ background: 'none', border: 'none', color: '#6366f1', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, cursor: 'pointer', marginBottom: 20 }}>
                    <FiArrowLeft /> Back to My Papers
                </button>
                <div style={{ background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', border: `1px solid ${C.border}`, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <FiClock size={32} />
                    </div>
                    <h3 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 800, color: C.text }}>Exam Locked</h3>
                    <p style={{ margin: '0 0 24px', fontSize: 15, color: C.sub, lineHeight: 1.6, maxWidth: 400, marginInline: 'auto' }}>{errorModal.message}</p>
                    <button
                        onClick={() => navigate('/teacher/papers')}
                        style={{ padding: '12px 24px', borderRadius: 12, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: font }}
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (!paper) return null;

    return (
        <div style={{ padding: '0px 10px 40px', fontFamily: font, margin: '0 auto', animation: 'fadeIn 0.3s ease' }}>
            {/* Header / Nav */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <button onClick={() => navigate('/teacher/papers')} style={{ background: 'none', border: 'none', color: '#6366f1', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                    <FiArrowLeft /> Back
                </button>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={downloadPDF} className="no-print" style={{ padding: '8px 16px', borderRadius: 20, background: '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.2)' }}>
                        <FiDownload size={16} /> Download Proper PDF
                    </button>
                    <div className="no-print" style={{ padding: '6px 14px', borderRadius: 20, background: getStatusStyle(paper.status).bg, color: getStatusStyle(paper.status).color, fontSize: 13, fontWeight: 700, textTransform: 'capitalize' }}>
                        {getStatusStyle(paper.status).label}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Paper Details Card */}
                <div className="tvp-paper-card">
                    <div className="tvp-paper-header">
                        <h1 className="tvp-paper-title">{paper.title}</h1>
                        <div style={{ fontSize: 13, color: C.sub }}>
                            {paper.subject_code} &middot; {paper.subject_name} &middot; {paper.batch_name}
                        </div>
                    </div>
                    <div className="tvp-info-grid">
                        <div><span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 3 }}>Department</span><strong style={{ fontSize: 13, color: C.text }}>{paper.department}</strong></div>
                        <div><span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 3 }}>Semester</span><strong style={{ fontSize: 13, color: C.text }}>Sem {paper.semester} ({paper.academic_year})</strong></div>
                        <div><span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 3 }}>Duration</span><strong style={{ fontSize: 13, color: C.text }}>{paper.duration_mins} mins</strong></div>
                        <div><span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 3 }}>Total Marks</span><strong style={{ fontSize: 13, color: C.text }}>{paper.total_marks}</strong></div>
                    </div>
                    {paper.instructions && (
                        <div className="tvp-instructions-wrap">
                            <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                                <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 4 }}>Instructions</span>
                                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{paper.instructions}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Submissions Section (Only for approved papers) */}
                {paper.status === 'approved' && (
                    <div className="no-print" style={{ padding: '14px 16px', background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: C.text }}>Student Submissions ({submissions.length})</h3>
                        {submissions.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 30, color: C.sub, fontSize: 13 }}>No students have submitted this exam yet.</div>
                        ) : (
                            <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>#</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Student Name</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Roll No.</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Reg No.</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Submitted</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Status</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Score</th>
                                            <th style={{ padding: '8px 10px', borderBottom: `1.5px solid ${C.border}`, color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {submissions.map((sub, idx) => (
                                            <tr key={sub.id} style={{ borderBottom: `1px solid #f1f5f9`, background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                <td style={{ padding: '7px 10px', fontWeight: 600, color: C.sub, fontSize: 11 }}>{idx + 1}</td>
                                                <td style={{ padding: '7px 10px', fontWeight: 700, color: C.text, fontSize: 12, whiteSpace: 'nowrap' }}>{sub.student_name || '—'}</td>
                                                <td style={{ padding: '7px 10px', color: C.text, fontSize: 11, whiteSpace: 'nowrap' }}>{sub.roll_number || '—'}</td>
                                                <td style={{ padding: '7px 10px', color: C.sub, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{sub.reg_number || '—'}</td>
                                                <td style={{ padding: '7px 10px', color: C.sub, fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(sub.submitted_at).toLocaleString()}</td>
                                                <td style={{ padding: '7px 10px' }}>
                                                    {sub.graded_at ? (
                                                        <span style={{ background: '#ecfdf5', color: '#10b981', padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>Evaluated</span>
                                                    ) : (
                                                        <span style={{ background: '#fffbeb', color: '#f59e0b', padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>Pending</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '7px 10px', fontWeight: 700, color: C.text, fontSize: 12 }}>
                                                    {sub.total_score !== null ? `${sub.total_score}/${paper.total_marks}` : '—'}
                                                </td>
                                                <td style={{ padding: '7px 10px' }}>
                                                    <button
                                                        onClick={() => navigate(`/teacher/submissions/${sub.id}`)}
                                                        style={{ padding: '4px 10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    >
                                                        {sub.graded_at ? 'Review' : 'Evaluate'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Questions List */}
                <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    Questions <span style={{ padding: '4px 10px', background: '#eef2ff', color: '#6366f1', borderRadius: 12, fontSize: 14 }}>{questions.length}</span>
                </h2>

                {questions.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: C.sub, background: '#fff', borderRadius: 16, border: `1px dashed ${C.border}` }}>No questions found.</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {questions.map((q, idx) => (
                            <div key={q.id || idx} style={{ padding: 20, background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <span style={{ fontWeight: 800, fontSize: 15, color: '#6366f1' }}>Q{q.question_no || idx + 1}.</span>
                                    <span style={{ fontSize: 12, color: C.sub, fontWeight: 700, background: C.bg, padding: '4px 10px', borderRadius: 12 }}>{q.marks} marks &middot; {q.question_type.toUpperCase()}</span>
                                </div>
                                <p style={{ margin: '0 0 16px', fontSize: 15, color: C.text, lineHeight: 1.6, fontWeight: 500 }}>{q.question_text}</p>

                                {q.image_url && (
                                    <div style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: '#f8fafc' }}>
                                        <img
                                            src={`${api.defaults.baseURL}${q.image_url}`}
                                            alt="Question"
                                            style={{ maxWidth: '100%', maxHeight: 400, display: 'block', margin: '0 auto', cursor: 'zoom-in' }}
                                            onClick={(e) => { e.stopPropagation(); window.open(`${api.defaults.baseURL}${q.image_url}`, '_blank'); }}
                                        />
                                    </div>
                                )}

                                {q.question_type === 'mcq' && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        {['A', 'B', 'C', 'D'].map(opt => {
                                            const val = q[`option_${opt.toLowerCase()}`];
                                            if (!val) return null;
                                            const isCorrect = q.correct_option && q.correct_option.toUpperCase() === opt;
                                            return (
                                                <div key={opt} style={{
                                                    padding: '10px 14px', borderRadius: 10, fontSize: 14,
                                                    background: isCorrect ? '#ecfdf5' : '#f8fafc',
                                                    border: `1px solid ${isCorrect ? '#a7f3d0' : C.border}`,
                                                    color: isCorrect ? '#047857' : C.sub,
                                                    display: 'flex', alignItems: 'center', gap: 8
                                                }}>
                                                    <span style={{ fontWeight: 800, width: 24, height: 24, borderRadius: 6, background: isCorrect ? '#10b981' : '#e2e8f0', color: isCorrect ? '#fff' : C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{opt}</span>
                                                    <span style={{ fontWeight: isCorrect ? 600 : 400, flex: 1, color: isCorrect ? '#065f46' : C.text }}>{val}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>


            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .tvp-paper-card {
                    background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.03);
                }
                .tvp-paper-header {
                    padding: 20px 24px; border-bottom: 1px solid #e2e8f0; background: #f8fafc;
                }
                .tvp-paper-title {
                    margin: 0 0 4px; font-size: 20px; font-weight: 900; color: #1e293b;
                }
                .tvp-info-grid {
                    padding: 16px 24px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
                }
                .tvp-instructions-wrap {
                    padding: 0 24px 16px;
                }
                @media (max-width: 768px) {
                    .tvp-paper-header { padding: 14px 16px; }
                    .tvp-paper-title { font-size: 16px; }
                    .tvp-info-grid { padding: 12px 16px; grid-template-columns: repeat(2, 1fr); gap: 10px; }
                    .tvp-instructions-wrap { padding: 0 16px 12px; }
                }
                @media print {
                    body { background: white !important; margin: 0; padding: 0; }
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    @page { margin: 20mm; }
                }
            `}</style>
        </div>
    );
};

export default TeacherViewPaper;
